import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { evaluateConnectingDotsRSI } from './ConnectingDotsRSI';
import { evaluateConnectingDots5MLows } from './ConnectingDots5MLows';
import { evaluateConnectingDotsItmFirst } from './ConnectingDotsItmFirst';
import { evaluateConnectingDotsItmTop5 } from './ConnectingDotsItmTop5';
import { evaluateMinMaxItmTop5 } from './MinMaxItmTop5';
import { evaluateConnectingDotsBreakout } from './ConnectingDotsBreakout';

export const evaluateConnectingDotsBase: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    let bullScore = 0;
    let bearScore = 0;
    let fallbackEntryStrike = 0;

    const evaluators = [
        evaluateConnectingDotsRSI,
        evaluateConnectingDots5MLows,
        evaluateConnectingDotsItmFirst,
        evaluateConnectingDotsItmTop5,
        evaluateMinMaxItmTop5,
        evaluateConnectingDotsBreakout
    ];

    evaluators.forEach(evaluate => {
        const rawResult = evaluate(data, currentUnixTime, config, context);
        const result = rawResult ? (Array.isArray(rawResult) ? rawResult[0] : rawResult) : null;

        if (result) {
            if (result.trade_type === 'BULL') bullScore++;
            else if (result.trade_type === 'BEAR') bearScore++;
            fallbackEntryStrike = result.strike; // Use the last generated strike
        }
    });

    // Simply match the UI by taking the dominant theme
    let status = 'Sideways';
    if (bullScore > bearScore) {
        status = 'BULL';
    } else if (bearScore > bullScore) {
        status = 'BEAR';
    }

    if (status === 'Sideways') return null;

    // Default entry logic
    const spot = data.spotPrice || data.index_ltp || data.ltp || 0;
    const atmStrike = Math.round(spot / context.gap) * context.gap;
    const entryStrike = fallbackEntryStrike || atmStrike;
    let entryPrice = 0;
    
    // Use ensureFlatOpts to support both raw and pre-flattened chain formats
    const liveRawOptsArr = context.chain || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);
    const optionType = status === 'BULL' ? 'CE' : 'PE';

    const atmRow = liveFlatOpts.find((r: any) => r.strike === entryStrike && r.type === optionType);
    if (atmRow) {
        entryPrice = atmRow.ltp || atmRow.close || atmRow.price || 0;

        if (entryPrice > 0) {
            return {
                trade_type: status as any,
                strike: entryStrike,
                option_type: status === 'BULL' ? 'CE' : 'PE',
                entry_price: entryPrice,
                remarks: `CD Base (Bull:${bullScore} Bear:${bearScore})`
            };
        }
    }

    return null;
};
